import { useEffect, useState } from "react";
import {
  Layout,
  Home,
  Layers,
  Sparkles,
  MoveUp,
  MoveDown,
  Check,
  Plus,
  Trash2,
  ExternalLink,
  Save,
  Radio,
  Eye,
  Sliders
} from "lucide-react";
import { LoadingState, EmptyState, ErrorState } from "../../components/LoadingState";
import { apiGet, apiSend } from "../../services/api";
import type { Article, Category, HomepageSection } from "../../types";

type AdminSection = {
  id: number;
  section_key: string;
  title: string;
  section_type: string;
  category_id: number | null;
  enabled: boolean;
  display_order: number;
  article_limit: number;
  settings_json?: Record<string, unknown>;
};

type SectionArticle = {
  section_id: number;
  article_id: number;
  slot: string;
  display_order: number;
};

export function HomepageCurationPage() {
  const [sections, setSections] = useState<AdminSection[]>([]);
  const [sectionArticles, setSectionArticles] = useState<SectionArticle[]>([]);
  const [availableArticles, setAvailableArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const [curationRes, articlesRes, catsRes] = await Promise.all([
        apiGet<{ sections: AdminSection[]; articles: SectionArticle[] }>("/api/homepage/admin"),
        apiGet<{ items: Article[] }>("/api/articles?pageSize=100"),
        apiGet<Category[]>("/api/categories")
      ]);
      setSections(curationRes.sections || []);
      setSectionArticles(curationRes.articles || []);
      setAvailableArticles(articlesRes.items || []);
      setCategories(catsRes || []);
      if (curationRes.sections?.length > 0 && !selectedSectionId) {
        setSelectedSectionId(curationRes.sections[0].id);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load homepage curation data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  async function handleToggleSection(section: AdminSection) {
    try {
      const updated = { ...section, enabled: !section.enabled };
      await apiSend("/api/homepage/sections", {
        method: "POST",
        body: JSON.stringify(updated)
      });
      setSections((prev) =>
        prev.map((s) => (s.id === section.id ? { ...s, enabled: !s.enabled } : s))
      );
    } catch (err: any) {
      alert(err.message || "Failed to update section");
    }
  }

  async function handleSaveSectionSettings(section: AdminSection) {
    try {
      setSaving(true);
      await apiSend("/api/homepage/sections", {
        method: "POST",
        body: JSON.stringify(section)
      });
      alert("Homepage layout configuration updated.");
    } catch (err: any) {
      alert(err.message || "Failed to save section");
    } finally {
      setSaving(false);
    }
  }

  async function handleAssignArticles(sectionId: number, articleIds: number[]) {
    try {
      setSaving(true);
      await apiSend(`/api/homepage/sections/${sectionId}/articles`, {
        method: "POST",
        body: JSON.stringify({ article_ids: articleIds })
      });
      await loadData();
    } catch (err: any) {
      alert(err.message || "Failed to assign curated articles");
    } finally {
      setSaving(false);
    }
  }

  const selectedSection = sections.find((s) => s.id === selectedSectionId);
  const currentAssignedIds = sectionArticles
    .filter((sa) => sa.section_id === selectedSectionId)
    .map((sa) => sa.article_id);

  return (
    <div className="admin-page-flow">
      {/* Header */}
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <Layout size={13} className="text-[#C99A2E]" />
            <span>EDITORIAL DESK</span>
          </div>
          <h1 className="admin-title">Front Page Curation</h1>
          <p className="admin-subtitle">
            Configure the visual layout, lead story hierarchy, and curated spotlight sections on the homepage.
          </p>
        </div>

        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="admin-btn-secondary"
        >
          <Eye size={15} />
          <span>Preview Public Homepage</span>
          <ExternalLink size={13} />
        </a>
      </header>

      {loading ? (
        <LoadingState label="Loading homepage architecture..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Sections List (5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-3">
            <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              Homepage Layout Sections ({sections.length})
            </h2>

            <div className="flex flex-col gap-2">
              {sections.map((sec) => (
                <div
                  key={sec.id}
                  onClick={() => setSelectedSectionId(sec.id)}
                  className={`admin-card-panel p-4 cursor-pointer transition-all border ${
                    selectedSectionId === sec.id
                      ? "border-[#C99A2E] bg-amber-50/20 shadow-xs"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#C99A2E]" />
                      <h3 className="font-bold text-slate-900 text-sm">{sec.title}</h3>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleSection(sec);
                      }}
                      className={`text-xs font-bold px-2 py-0.5 rounded-full border transition-colors ${
                        sec.enabled
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-500 border-slate-200"
                      }`}
                    >
                      {sec.enabled ? "Active" : "Disabled"}
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
                    <span className="font-mono text-[11px]">Type: {sec.section_type}</span>
                    <span>Max {sec.article_limit} stories</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Section Detail & Article Selector (7 cols) */}
          <div className="lg:col-span-7">
            {selectedSection ? (
              <div className="admin-card-panel">
                <div className="flex items-center justify-between pb-4 border-b border-slate-200 mb-4">
                  <div>
                    <h2 className="text-sm font-bold text-slate-900">
                      Configure: {selectedSection.title}
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Section key: <span className="font-mono">{selectedSection.section_key}</span>
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handleSaveSectionSettings(selectedSection)}
                    className="admin-btn-primary"
                  >
                    <Save size={14} />
                    <span>{saving ? "Saving..." : "Save Settings"}</span>
                  </button>
                </div>

                {/* Section Attributes Form */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                      Section Headline Title
                    </label>
                    <input
                      type="text"
                      value={selectedSection.title}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSections((prev) =>
                          prev.map((s) => (s.id === selectedSection.id ? { ...s, title: val } : s))
                        );
                      }}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1 uppercase tracking-wider">
                      Story Limit
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={selectedSection.article_limit}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        setSections((prev) =>
                          prev.map((s) => (s.id === selectedSection.id ? { ...s, article_limit: val } : s))
                        );
                      }}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
                    />
                  </div>
                </div>

                {/* Curated Story Selector */}
                <div className="border-t border-slate-200 pt-4">
                  <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-2">
                    Curated Article Assignment
                  </h3>
                  <p className="text-xs text-slate-500 mb-4">
                    Select specific published stories to pin to this homepage block, or leave automatic for continuous flow.
                  </p>

                  <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg">
                    {availableArticles.map((article) => {
                      const isAssigned = currentAssignedIds.includes(article.id);
                      return (
                        <div
                          key={article.id}
                          onClick={() => {
                            const nextIds = isAssigned
                              ? currentAssignedIds.filter((id) => id !== article.id)
                              : [...currentAssignedIds, article.id];
                            handleAssignArticles(selectedSection.id, nextIds);
                          }}
                          className={`p-3 flex items-center justify-between cursor-pointer transition-colors ${
                            isAssigned ? "bg-amber-50/50" : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isAssigned}
                              onChange={() => {}}
                              className="rounded border-slate-300 text-[#C99A2E] focus:ring-[#C99A2E]"
                            />
                            <div>
                              <p className="text-xs font-semibold text-slate-900 line-clamp-1">
                                {article.title}
                              </p>
                              <span className="text-[11px] text-slate-400">
                                {article.category.name} • {article.author.name}
                              </span>
                            </div>
                          </div>

                          <span className={`status-pill status-${article.status} shrink-0`}>
                            <span>{article.status}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <EmptyState title="Select a section" body="Select a homepage layout section on the left to configure." />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
